import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowLeft, MapPin, Shield, FlaskConical, Stethoscope, ArrowRight, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHOP_URL = 'https://shop.paramountpeptides.com/MARKBECKER';
const GPO_URL = 'https://alphabiomedlabs.com/pages/register?ref=MARKBECKER';

const PeptideCard = ({ peptide }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <h4 className="font-bold text-gray-900">{peptide.name}</h4>
    <p className="text-sm text-gray-600 mt-1">{peptide.use}</p>
  </div>
);

const CitySection = ({ city, stateName }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <div className="flex items-center gap-2 mb-3">
      <MapPin size={18} className="text-blue-600" />
      <h3 className="text-xl font-bold text-gray-900">{city.city}, {stateName}</h3>
    </div>
    <p className="text-gray-700 leading-relaxed">{city.content}</p>
  </div>
);

const StateIntro = ({ name, intro }) => (
  <section className="bg-white rounded-xl shadow-sm p-8" data-testid="state-intro">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">Peptide Therapy in {name}</h2>
    <p className="text-gray-700 leading-relaxed text-lg">{intro}</p>
  </section>
);

const StateLegal = ({ name, legalStatus }) => (
  <section className="bg-white rounded-xl shadow-sm p-8" data-testid="state-legal">
    <div className="flex items-center gap-3 mb-4">
      <Shield size={24} className="text-green-600" />
      <h2 className="text-2xl font-bold text-gray-900">Legal Status in {name}</h2>
    </div>
    <p className="text-gray-700 leading-relaxed">{legalStatus}</p>
  </section>
);

const StatePeptides = ({ name, peptides }) => (
  <section className="bg-white rounded-xl shadow-sm p-8" data-testid="state-peptides">
    <div className="flex items-center gap-3 mb-6">
      <FlaskConical size={24} className="text-blue-600" />
      <h2 className="text-2xl font-bold text-gray-900">Popular Peptide Treatments in {name}</h2>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {peptides.map(p => <PeptideCard key={p.name} peptide={p} />)}
    </div>
    <div className="mt-6">
      <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold" data-testid="shop-link">
        Browse Research-Grade Peptides <ExternalLink className="ml-2" size={16} />
      </a>
    </div>
  </section>
);

const StateHowToFind = ({ name, howToFind }) => (
  <section className="bg-white rounded-xl shadow-sm p-8" data-testid="state-how-to-find">
    <div className="flex items-center gap-3 mb-4">
      <Stethoscope size={24} className="text-purple-600" />
      <h2 className="text-2xl font-bold text-gray-900">How to Find a Provider in {name}</h2>
    </div>
    <p className="text-gray-700 leading-relaxed">{howToFind}</p>
  </section>
);

const StateExpect = ({ whatToExpect }) => (
  <section className="bg-white rounded-xl shadow-sm p-8" data-testid="state-expect">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">What to Expect at Your First Visit</h2>
    <p className="text-gray-700 leading-relaxed">{whatToExpect}</p>
  </section>
);

const StateCTAs = ({ stateName }) => (
  <div className="grid md:grid-cols-2 gap-6">
    <div className="bg-gradient-to-r from-gray-900 to-slate-800 rounded-xl p-8 text-white">
      <h3 className="text-2xl font-bold mb-3">Shop Premium Peptides</h3>
      <p className="text-gray-300 mb-5">Research-grade peptides from trusted sources, delivered nationwide.</p>
      <a href={SHOP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-all" data-testid="state-shop-cta">
        Visit Shop <ExternalLink className="ml-2" size={18} />
      </a>
    </div>
    <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-8 text-white">
      <h3 className="text-2xl font-bold mb-3">Licensed Provider?</h3>
      <p className="text-green-100 mb-5">Join our network and reach patients in {stateName} seeking peptide therapy.</p>
      <a href={GPO_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-6 py-3 bg-white text-green-700 rounded-lg font-semibold hover:bg-green-50 transition-all" data-testid="state-provider-cta">
        Sign Up <ArrowRight className="ml-2" size={18} />
      </a>
    </div>
  </div>
);

const ClinicStatePage = () => {
  const { stateSlug } = useParams();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadState = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clinic-states/${stateSlug}`);
      setState(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Error loading state:', err);
    } finally {
      setLoading(false);
    }
  }, [stateSlug]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  if (loading) {
    return <div className="min-h-screen bg-gray-50"><Navigation /><div className="max-w-4xl mx-auto px-4 py-20 text-center text-xl text-gray-600">Loading...</div></div>;
  }

  if (!state) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <div className="text-xl text-gray-600">State not found</div>
          <Link to="/peptide-clinics" className="text-blue-600 hover:underline mt-4 inline-block">Browse All States</Link>
        </div>
      </div>
    );
  }

  const pageUrl = `https://www.peptidesciences.ai/peptide-clinics/${state.slug}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `Peptide Therapy Clinics in ${state.name}`,
    "description": state.meta_description,
    "url": pageUrl,
    "about": { "@type": "MedicalTherapy", "name": "Peptide Therapy", "description": `Peptide therapy services available in ${state.name}` },
    "areaServed": { "@type": "State", "name": state.name, "containedInPlace": { "@type": "Country", "name": "United States" } }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{`Peptide Therapy Clinics in ${state.name} | Find Providers in ${state.cities[0]} & More | PeptideSciences.ai`}</title>
        <meta name="description" content={state.meta_description} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={`Peptide Therapy Clinics in ${state.name} | PeptideSciences.ai`} />
        <meta property="og:description" content={state.meta_description} />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`Peptide Clinics in ${state.name}`} />
        <meta name="twitter:description" content={state.meta_description} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <Navigation />

      <section className="bg-gradient-to-r from-gray-900 via-slate-800 to-gray-900 text-white py-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'url(https://customer-assets.emergentagent.com/job_07859234-c0aa-4e8d-94c2-c8d61ccd3354/artifacts/a25q64nh_Peptide%20Background.png)', backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-slate-800/85 to-gray-900/90"></div>
        <div className="max-w-5xl mx-auto relative z-10">
          <Link to="/peptide-clinics" className="inline-flex items-center text-gray-300 hover:text-white mb-6" data-testid="back-to-clinics">
            <ArrowLeft className="mr-2" size={20} /> All States
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <MapPin size={28} className="text-blue-400" />
            <span className="text-blue-400 font-semibold uppercase tracking-wide text-sm">{state.abbr}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight" data-testid="state-title">Peptide Therapy Clinics in {state.name}</h1>
          <p className="text-lg text-gray-300">Find licensed peptide providers in {state.cities.join(', ')}</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-12 space-y-10">
        <StateIntro name={state.name} intro={state.intro} />
        <StateLegal name={state.name} legalStatus={state.legal_status} />
        <StatePeptides name={state.name} peptides={state.popular_peptides} />
        <StateHowToFind name={state.name} howToFind={state.how_to_find} />
        <StateExpect whatToExpect={state.what_to_expect} />

        <section data-testid="state-cities">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Peptide Clinics by City in {state.name}</h2>
          <div className="space-y-4">
            {state.city_sections.map(city => <CitySection key={city.city} city={city} stateName={state.name} />)}
          </div>
        </section>

        <StateCTAs stateName={state.name} />

        <div className="text-center pt-8">
          <Link to="/peptide-clinics" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold text-lg" data-testid="browse-all-states">
            <ArrowLeft className="mr-2" size={18} /> Browse All 50 States
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ClinicStatePage;
